import React, { useEffect, useState } from "react";
import { Modal, Button, message, Spin, Form, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import drEasyApi from "../../Api/Customapi";
import dayjs from 'dayjs';

// Import sub-components
import PatientHeader from "./PatientHeader";
import ServicesStep from "./ServicesStep";
import ReportCard from "./ReportCard";
import AddColumnModal from "./Modals/AddColumnModal";
import EditColumnModal from "./Modals/EditColumnModal";
import EditCellModal from "./Modals/EditCellModal";
// import ScheduleModal from "./Modals/ScheduleModal";
import InvoiceSummaryStep from "./InvoiceSummaryStep";

// Import utilities
import { calculateInvoiceTotals, getTextFor, getPhotoIdFor } from "../../utils/reportUtils";
import ScheduleModal from "../Modals/SchedualModal";

// Responsive hook
const useResponsive = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
        const updateSize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 640);
            setIsTablet(width >= 640 && width < 1024);
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return { isMobile, isTablet };
};

const AddReportModal = ({
    open,
    onClose,
    patient,
    setPatient,
    onRefreshSchedules,
    change,
    setChange,
    booking,
    onServicesNext
}) => {
    const { isMobile, isTablet } = useResponsive();

    const [columns, setColumns] = useState([]);
    const [reportRows, setReportRows] = useState([]);
    const [reportId, setReportId] = useState(null);
    const [reportMessage, setReportMessage] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const [currentStep, setCurrentStep] = useState(0);
    const [doctorServices, setDoctorServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [paidAmount, setPaidAmount] = useState(0);
    const [visitPrice, setVisitPrice] = useState(0);
    const [discountAmount, setDiscountAmount] = useState(0);
const [editingSchedule, setEditingSchedule] = useState(null);

    const [loadingReport, setLoadingReport] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [updatingCellId, setUpdatingCellId] = useState(null);
    const [savingColumn, setSavingColumn] = useState(false);
    const [updatingCell, setUpdatingCell] = useState(false);
    const [updatingColumn, setUpdatingColumn] = useState(false);

    const [showAddColumnModal, setShowAddColumnModal] = useState(false);
    const [newColumnTitle, setNewColumnTitle] = useState("");
    const [newIsPrivate, setNewIsPrivate] = useState(false);
    const [cellValues, setCellValues] = useState({});
    const [cellPhotos, setCellPhotos] = useState({});

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editColumn, setEditColumn] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editPrivate, setEditPrivate] = useState(false);

    const [editCellModalOpen, setEditCellModalOpen] = useState(false);
    const [editingCell, setEditingCell] = useState(null);

    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [extractedScheduleData, setExtractedScheduleData] = useState(null);
    const [assigningSchedule, setAssigningSchedule] = useState(false);
    const [scheduleForm] = Form.useForm();
    const queryClient = useQueryClient();

    const fetchReports = async () => {
        try {
            setLoadingReport(true);
            const res = await drEasyApi.get("/api/Reports/GetUserReport", {
                params: { UserId: patient.profileId },
            });

            setColumns(res.data.columns || []);
            setReportRows(res.data.rows || []);
            setReportId(res.data.id);
        } catch (err) {
            if (err.response?.status === 404) {
                setReportMessage("Patient or doctor not found.");
            } else if (err.response?.status === 204) {
                setReportMessage("This patient doesn't have any completed bookings yet.");
            } else if (err.response?.status === 403) {
                setReportMessage("You are not authorized to view this report.");
            } else {
                setReportMessage("Failed to load report data.");
            }
            console.error("Error fetching report:", err);
        } finally {
            setLoadingReport(false);
        }
    };

    // Fetch doctor services
    const fetchDoctorServices = async () => {
        setServicesLoading(true);
        try {
            const response = await drEasyApi.get('/api/Services/GetMyServices');
            setDoctorServices(response.data);
        } catch (error) {
            console.error('Error fetching services:', error);
            message.error('Error fetching services');
        } finally {
            setServicesLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchReports();
        }
    }, [open, refreshKey]);

    const handleNext = async () => {
        if (currentStep === 0) {
            await fetchDoctorServices();
            setCurrentStep(1);
        } else if (currentStep === 1) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            setCurrentStep(3);
        } else if (currentStep === 3) {
            setCurrentStep(4);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleModalClose = () => {
        setCurrentStep(0);
        setSelectedServices([]);
        setPaidAmount(0);
        setVisitPrice(0);
        setDiscountAmount(0);
        onClose();
    };

    const handleCompleteBooking = async () => {
       
        try {
            setCreatingInvoice(true);

            const invoicePayload = {
                bookingId: booking?.id,
                visitPrice: visitPrice,
                paidAmount: paidAmount,
                discountAmount: discountAmount,
                serviceIds: selectedServices
            };

            await drEasyApi.post("/api/BookingInvoice/Create", invoicePayload);
            message.success("Invoice created successfully!");
            queryClient.invalidateQueries(['bookings']);

            if (onServicesNext) {
                onServicesNext(selectedServices);
            }

            handleModalClose();
        } catch (error) {
            console.error("Error creating invoice:", error);
            message.error(error.response?.data?.message || "Failed to create invoice.");
        } finally {
            setCreatingInvoice(false);
        }
    };

    const openAddColumnModal = () => {
        setNewColumnTitle("");
        setCellValues({});
        setCellPhotos({});
        setNewIsPrivate(false);
        setShowAddColumnModal(true);
    };

    const handleAddColumnAndCells = async () => {
        try {
            let currentReportId = patient?.reportId || reportId;
            setSavingColumn(true);

            if (!currentReportId) {
                const createReportRes = await drEasyApi.post("/api/Reports/CreateReport", {
                    userId: patient.profileId,
                });

                currentReportId = createReportRes.data;
                setReportId(currentReportId);

                if (setPatient) {
                    setPatient((prev) => ({ ...prev, reportId: currentReportId }));
                }
            }

            const columnRes = await drEasyApi.post("/api/Reports/AddColumn", {
                reportId: currentReportId,
                title: newColumnTitle,
                isPrivate: newIsPrivate,
            });

            await fetchReports();
            const newColId = columnRes?.data;

         
            await fetchReports();
            setShowAddColumnModal(false);
            message.success("Section added successfully!");
        } catch (err) {
            console.error("Error adding column or cells:", err);
            message.error("Failed to add column or update cells.");
        } finally {
            setSavingColumn(false);
        }
    };

    const openEditModal = (column) => {
        setEditColumn(column);
        setEditTitle(column.title);
        setEditPrivate(column.isPrivate);
        setEditModalOpen(true);
    };

    const handleUpdateColumn = async () => {
        try {
            setUpdatingColumn(true);

            await drEasyApi.put("/api/Reports/EditCloumn", {
                columnId: editColumn.id,
                title: editTitle,
                isPrivate: editPrivate,
            });

            await fetchReports();
            message.success("Column updated successfully");
            setEditModalOpen(false);
        } catch (err) {
            console.error("Failed to update column", err);
            message.error("Failed to update column");
        } finally {
            setUpdatingColumn(false);
        }
    };

    const handleDeleteColumn = async (columnId) => {
        try {
            await drEasyApi.delete(`/api/Reports/DeleteColumn/${columnId}`);
            await fetchReports();
            message.success("Section deleted successfully");
        } catch (err) {
            console.error("Failed to delete column", err);
            message.error("Failed to delete column");
        }
    };

    const handleUpdateCell = async ({ bookingId, columnId, text, photoFile }) => {
        try {
            let photoId = null;
            setUpdatingCell(true);
            setUpdatingCellId(`${bookingId}-${columnId}`);

            if (photoFile) {
                const formData = new FormData();
                formData.append("file", photoFile);

                const uploadRes = await drEasyApi.post("/api/Storage/UploadImage", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                photoId = uploadRes?.data?.id;
            }

            const res = await drEasyApi.post("/api/Reports/AddOrUpdateCell", {
                bookingId: booking?.id,
                columnId,
                text,
                photoId,
            });

            await fetchReports();

            if (res.data.extractedSchedule) {
                setExtractedScheduleData(res.data.extractedSchedule);
                setScheduleModalOpen(true);
                populateScheduleForm(res.data.extractedSchedule);
            }

            message.success("Cell updated");
            setEditCellModalOpen(false);
        } catch (err) {
            console.error("Failed to update cell", err);
            message.error("Error updating cell");
        } finally {
            setUpdatingCell(false);
            setUpdatingCellId(null);
        }
    };

  const populateScheduleForm = (scheduleData) => {
    const items = Array.isArray(scheduleData) ? scheduleData :
        scheduleData.items ? scheduleData.items : [scheduleData];

    if (items.length > 0) {
        const item = items[0];

        // Convert reminder times to HH:mm format strings
        const reminderTimesFormatted = item.reminderTimes?.map(time => {
            if (typeof time === 'string') {
                return time.includes('T') ? time.split('T')[1].substring(0, 5) : time;
            }
            const date = new Date(time);
            return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        }) || [];

        setEditingSchedule({
            name: item.name || "",
            type: item.type || 1,
            isActive: item.isActive !== undefined ? item.isActive : true,
            repeatDays: item.repeatDays || 0,
            reminderTimes: reminderTimesFormatted,
            reminderTimesJson: JSON.stringify(reminderTimesFormatted),
            startDate: item.startDate || new Date().toISOString(),
            endDate: item.endDate || null,
            reminderDays: item.reminderDays || []
        });
    }
};

   const handleAssignSchedule = async (scheduleData) => {
    try {
        setAssigningSchedule(true);

        const payload = {
            userId: patient.accountId,
            items: [{
                name: scheduleData.name,
                type: scheduleData.type,
                isActive: scheduleData.isActive !== undefined ? scheduleData.isActive : true,
                repeatDays: scheduleData.repeatDays || null,
                reminderTimes: scheduleData.reminderTimes || [],
                startDate: scheduleData.startDate || new Date().toISOString(),
                endDate: scheduleData.endDate || null,
                reminderDays: scheduleData.reminderDays || []
            }]
        };

        await drEasyApi.post("/api/Schedule/AssignList", payload);

        message.success("Schedule assigned successfully!");
        if (onRefreshSchedules) {
            queryClient.invalidateQueries(['schedules', patient.accountId]);
        }
        setChange(prev => !prev);
        setScheduleModalOpen(false);
        setEditingSchedule(null);
        setExtractedScheduleData(null);

    } catch (err) {
        console.error("Failed to assign schedule:", err);
        message.error(err.response?.data[0] || "Failed to assign schedule");
    } finally {
        setAssigningSchedule(false);
    }
};

    const { subtotal, total } = calculateInvoiceTotals(doctorServices, selectedServices);
    const totalWithDiscount = visitPrice + subtotal - discountAmount;
    const remainingBalance = totalWithDiscount - paidAmount;

    const getTextForCard = (columnId, bookingId) => getTextFor(reportRows, columnId, bookingId);
    const getPhotoIdForCard = (columnId, bookingId) => getPhotoIdFor(reportRows, columnId, bookingId);

    return (
        <>
            <Modal
                closable={false}
                open={open}
                footer={
                    <div className={`flex justify-between items-center ${isMobile ? 'flex-col gap-2' : ''}`}>
                        <div className={isMobile ? 'w-full' : ''}>
                            {currentStep > 0 && (
                                <Button 
                                    onClick={handlePrevious} 
                                    size={isMobile ? 'middle' : 'large'}
                                    block={isMobile}
                                >
                                    Previous
                                </Button>
                            )}
                        </div>
                        <div className={isMobile ? 'w-full' : ''}>
                            {currentStep === 0 && (
                                <Button
                                    type="primary"
                                    onClick={handleNext}
                                    size={isMobile ? 'middle' : 'large'}
                                    style={{ backgroundColor: '#298E9F' }}
                                    block={isMobile}
                                >
                                    Next
                                </Button>
                            )}
                            {currentStep === 1 && (
                                <Button
                                    type="primary"
                                    onClick={handleNext}
                                    size={isMobile ? 'middle' : 'large'}
                                    disabled={selectedServices.length === 0}
                                    style={{ backgroundColor: '#298E9F' }}
                                    block={isMobile}
                                >
                                    {isMobile ? 'Next' : 'Next: Set Visit Price'}
                                </Button>
                            )}
                            {currentStep === 2 && (
                                <Button
                                    type="primary"
                                    onClick={handleNext}
                                    size={isMobile ? 'middle' : 'large'}
                                    disabled={ visitPrice < 0}
                                    style={{ backgroundColor: '#298E9F' }}
                                    block={isMobile}
                                >
                                    {isMobile ? 'Next' : 'Next: Payment'}
                                </Button>
                            )}
                            {currentStep === 3 && (
                                <Button
                                    type="primary"
                                    onClick={handleNext}
                                    size={isMobile ? 'middle' : 'large'}
                                    style={{ backgroundColor: '#298E9F' }}
                                    block={isMobile}
                                >
                                    {isMobile ? 'Review' : 'Review Summary'}
                                </Button>
                            )}
                            {currentStep === 4 && (
                                <Button
                                    type="primary"
                                    onClick={handleCompleteBooking}
                                    size={isMobile ? 'middle' : 'large'}
                                    loading={creatingInvoice}
                                    style={{ backgroundColor: '#298E9F' }}
                                    block={isMobile}
                                >
                                    {isMobile ? 'Complete' : 'Confirm & Complete'}
                                </Button>
                            )}
                        </div>
                    </div>
                }
                width={isMobile ? '100vw' : isTablet ? '90vw' : 1000}
                title={null}
                bodyStyle={{ padding: 0 }}
                className="report-modal"
                style={isMobile ? { top: 0, paddingBottom: 0 } : {}}
            >
                <Spin spinning={loadingReport}>
                    <div className="rounded-t-lg">
                        <PatientHeader patient={patient} currentStep={currentStep} />

                        <div 
                            className={isMobile ? 'p-3' : 'p-6'} 
                            style={{ 
                                maxHeight: isMobile ? '70vh' : '75vh', 
                                overflowY: 'auto' 
                            }}
                        >
                            {/* Step 0: Report Details */}
                            {currentStep === 0 && (
                                <>
                                    <div className={`flex ${isMobile ? 'flex-col' : ''} justify-between items-center gap-${isMobile ? '3' : '4'}`}>
                                        <div className="bg-yellowCard border border-yellowBorder flex-1 rounded-lg p-2 w-full">
                                            <h1 className={`font-bold ${isMobile ? 'text-base' : 'md:text-lg'}`}>Date:</h1>
                                            <span className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>
                                                {new Date(booking?.bookingDate).toLocaleDateString('en-US')}
                                            </span>
                                        </div>
                                        <button
                                            onClick={openAddColumnModal}
                                            className={`bg-primary text-white font-semibold p-2 rounded-lg ${
                                                isMobile ? 'w-full text-base' : 'w-44 text-lg'
                                            } flex items-center justify-center gap-2`}
                                        >
                                            Add Section <PlusOutlined />
                                        </button>
                                    </div>

                                    {!reportId && reportRows.length === 0 ? (
                                        <div className="text-center py-16">
                                            <div className="mb-6">
                                                <div className={`${isMobile ? 'w-16 h-16' : 'w-24 h-24'} mx-auto bg-gradient-to-r from-primary to-primary rounded-full flex items-center justify-center shadow-xl mb-4`}>
                                                    <svg className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12'} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <h3 className={`font-bold text-gray-800 mb-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>No Report Yet</h3>
                                                <p className={`text-gray-600 mb-6 max-w-md mx-auto ${isMobile ? 'text-sm px-4' : ''}`}>
                                                    {reportMessage || "Create a new report to start tracking medical history."}
                                                </p>
                                            </div>
                                            <Button
                                                type="primary"
                                                size={isMobile ? 'middle' : 'large'}
                                                icon={<PlusOutlined />}
                                                onClick={async () => {
                                                    try {
                                                        setLoadingReport(true);
                                                        const createReportRes = await drEasyApi.post("/api/Reports/CreateReport", {
                                                            userId: patient.profileId,
                                                        });
                                                        const newReportId = createReportRes.data;
                                                        message.success("Report created successfully!");
                                                        setReportId(newReportId);
                                                        if (setPatient) {
                                                            setPatient((prev) => ({ ...prev, reportId: newReportId }));
                                                        }
                                                        setRefreshKey((prev) => prev + 1);
                                                    } catch (err) {
                                                        console.error("Error creating report:", err);
                                                        message.error("Failed to create report.");
                                                    } finally {
                                                        setLoadingReport(false);
                                                    }
                                                }}
                                                style={{ backgroundColor: '#298E9F' }}
                                            >
                                                Create New Report
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {reportRows.length > 0 && (
                                                <ReportCard
                                                    row={reportRows.find((r)=>r.bookingId === booking?.id)}
                                                    columns={columns}
                                                    getTextFor={getTextForCard}
                                                    getPhotoIdFor={getPhotoIdForCard}
                                                    updatingCellId={updatingCellId}
                                                    onEditColumn={openEditModal}
                                                    onDeleteColumn={handleDeleteColumn}
                                                    onEditCell={(cell) => {
                                                        setEditingCell(cell);
                                                        setEditCellModalOpen(true);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Step 1: Services */}
                            {currentStep === 1 && (
                                <ServicesStep
                                    doctorServices={doctorServices}
                                    selectedServices={selectedServices}
                                    setSelectedServices={setSelectedServices}
                                    servicesLoading={servicesLoading}
                                />
                            )}

                            {/* Step 2: Set Visit Price */}
                            {currentStep === 2 && (
                                <div className={`space-y-${isMobile ? '4' : '6'}`}>
                                    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl ${isMobile ? 'p-4' : 'p-6'} border border-blue-200`}>
                                        <h3 className={`font-bold text-gray-800 mb-4 flex items-center gap-2 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                                            Set Visit Price
                                        </h3>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`block font-medium text-gray-700 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                                    Visit Price (LE) 
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={visitPrice}
                                                    onChange={(e) => setVisitPrice(parseFloat(e.target.value) || 0)}
                                                    prefix={<span className="text-[#298E9F] font-semibold">LE</span>}
                                                    size={isMobile ? 'middle' : 'large'}
                                                    min={0}
                                                    step="0.01"
                                                    placeholder="Enter visit price"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Payment */}
                            {currentStep === 3 && (
                                <div className={`space-y-${isMobile ? '4' : '5'} p-0` }>
                                    {/* Pricing Section */}
                                    <div className="bg-[#e6fdff] border border-[#298E9F]/20 rounded-lg p-4">
                                        <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'} mb-4`}>
                                            <div>
                                                <p className={`text-gray-600 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Visit Price</p>
                                                <Input
                                                    type="number"
                                                    value={visitPrice}
                                                    onChange={(e) => setVisitPrice(parseFloat(e.target.value) || 0)}
                                                    prefix="LE"
                                                    size={isMobile ? 'middle' : 'large'}
                                                    min={0}
                                                    step="0.01"
                                                />
                                            </div>
                                            <div>
                                                <p className={`text-gray-600 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Discount</p>
                                                <Input
                                                    type="number"
                                                    value={discountAmount}
                                                    onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                                                    prefix="LE"
                                                    size={isMobile ? 'middle' : 'large'}
                                                    min={0}
                                                    step="0.01"
                                                />
                                            </div>
                                        </div>
                                        <div className="pt-3 border-t border-[#298E9F]/20">
                                            <div className="flex justify-between items-center">
                                                <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Total Amount</p>
                                                <p className={`font-bold text-[#298E9F] ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                                                    {totalWithDiscount.toFixed(2)} LE
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Services Table */}
                                    {selectedServices.length > 0 && (
                                        <div>
                                            <h4 className={`font-semibold text-gray-800 mb-3 ${isMobile ? 'text-sm' : ''}`}>Services</h4>
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <table className="w-full">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className={`text-left py-2 px-3 font-semibold text-gray-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>Service</th>
                                                            <th className={`text-right py-2 px-3 font-semibold text-gray-700 ${isMobile ? 'text-xs w-20' : 'text-sm w-44'}`}>Price</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {doctorServices
                                                            .filter(service => selectedServices.includes(service.id))
                                                            .map((service, index) => (
                                                                <tr key={service.id || index} className="hover:bg-gray-50">
                                                                    <td className={`py-2 px-3 text-gray-800 ${isMobile ? 'text-xs' : ''}`}>{service.title}</td>
                                                                    <td className={`py-2 px-3 text-right font-semibold text-gray-800 ${isMobile ? 'text-xs' : ''}`}>
                                                                        {service.price?.toFixed(2)} LE
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Payment Input */}
                                    <div className="bg-[#FFFBEB] border-2 border-[#FDE68A] rounded-lg p-4">
                                        <h4 className={`font-semibold text-gray-800 mb-3 ${isMobile ? 'text-sm' : ''}`}>Payment Amount</h4>
                                        <div className={`flex ${isMobile ? 'flex-col' : 'items-center'} gap-${isMobile ? '3' : '6'}`}>
                                            <Input
                                                type="number"
                                                value={paidAmount}
                                                onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                                                prefix={<span className="text-[#298E9F] font-semibold">LE</span>}
                                                size={isMobile ? 'middle' : 'large'}
                                                min={0}
                                                max={totalWithDiscount}
                                                step="0.01"
                                                placeholder="Enter amount or leave 0 to pay later"
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    size={isMobile ? 'middle' : 'large'}
                                                    onClick={() => setPaidAmount(totalWithDiscount)}
                                                    className="flex-1"
                                                >
                                                    Full
                                                </Button>
                                                <Button
                                                    size={isMobile ? 'middle' : 'large'}
                                                    onClick={() => setPaidAmount(0)}
                                                    danger
                                                    className="flex-1"
                                                >
                                                    Clear
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary */}
                                    <div className="space-y-2 pt-2">
                                        <div className="flex justify-between items-center py-2">
                                            <span className={`text-gray-600 ${isMobile ? 'text-xs' : ''}`}>Subtotal</span>
                                            <span className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                                                {(visitPrice + subtotal).toFixed(2)} LE
                                            </span>
                                        </div>

                                        {discountAmount > 0 && (
                                            <div className="flex justify-between items-center py-2">
                                                <span className={`text-gray-600 ${isMobile ? 'text-xs' : ''}`}>Discount</span>
                                                <span className={`font-semibold text-red-600 ${isMobile ? 'text-sm' : ''}`}>
                                                    -{discountAmount.toFixed(2)} LE
                                                </span>
                                            </div>
                                        )}

                                        {paidAmount > 0 && (
                                            <div className="flex justify-between items-center py-2">
                                                <span className={`text-gray-600 ${isMobile ? 'text-xs' : ''}`}>Paying Now</span>
                                                <span className={`font-semibold text-green-600 ${isMobile ? 'text-sm' : ''}`}>
                                                    {paidAmount.toFixed(2)} LE
                                                </span>
                                            </div>
                                        )}

                                        <div className={`flex justify-between items-center p-3 rounded-lg mt-3 ${
                                            remainingBalance <= 0 && paidAmount > 0
                                                ? 'bg-green-50 border border-green-200'
                                                : 'bg-[#FFFBEB] border border-[#FDE68A]'
                                        }`}>
                                            <span className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>Balance Due</span>
                                            <span className={`font-bold ${isMobile ? 'text-lg' : 'text-xl'} ${
                                                remainingBalance <= 0 && paidAmount > 0 ? 'text-green-600' : 'text-[#FEBA17]'
                                            }`}>
                                                {remainingBalance.toFixed(2)} LE
                                            </span>
                                        </div>

                                        {/* Status Messages */}
                                        {remainingBalance <= 0 && paidAmount > 0 && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                                                <span className={`text-green-600 ${isMobile ? 'text-lg' : 'text-xl'} mr-2`}>✓</span>
                                                <span className={`text-green-700 font-semibold ${isMobile ? 'text-sm' : ''}`}>Fully Paid</span>
                                            </div>
                                        )}

                                        {paidAmount === 0 && (
                                            <div className="bg-[#e6fdff] border border-[#298E9F]/30 rounded-lg p-3 text-center">
                                                <p className={`text-[#298E9F] font-semibold m-0 ${isMobile ? 'text-xs' : ''}`}>
                                                    No payment - Full amount can be paid later
                                                </p>
                                            </div>
                                        )}

                                        {paidAmount > 0 && remainingBalance > 0 && (
                                            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 text-center">
                                                <p className={`text-[#FEBA17] font-semibold m-0 ${isMobile ? 'text-xs' : ''}`}>
                                                    Partial Payment - Balance: {remainingBalance.toFixed(2)} LE
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Summary */}
                            {currentStep === 4 && (
                                <InvoiceSummaryStep
                                    patient={patient}
                                    booking={booking}
                                    doctorServices={doctorServices}
                                    selectedServices={selectedServices}
                                    subtotal={subtotal}
                                    total={totalWithDiscount}
                                    visitPrice={visitPrice}
                                    paidAmount={paidAmount}
                                    discountAmount={discountAmount}
                                />
                            )}
                        </div>
                    </div>
                </Spin>
            </Modal>

            {/* Sub-Modals */}
            <AddColumnModal
                open={showAddColumnModal}
                onClose={() => setShowAddColumnModal(false)}
                onSave={handleAddColumnAndCells}
                reportRows={reportRows}
                newColumnTitle={newColumnTitle}
                setNewColumnTitle={setNewColumnTitle}
                newIsPrivate={newIsPrivate}
                setNewIsPrivate={setNewIsPrivate}
                cellValues={cellValues}
                setCellValues={setCellValues}
                cellPhotos={cellPhotos}
                setCellPhotos={setCellPhotos}
                savingColumn={savingColumn}
            />

            <EditColumnModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onUpdate={handleUpdateColumn}
                editTitle={editTitle}
                setEditTitle={setEditTitle}
                editPrivate={editPrivate}
                setEditPrivate={setEditPrivate}
                updatingColumn={updatingColumn}
            />

            <EditCellModal
                open={editCellModalOpen}
                onClose={() => setEditCellModalOpen(false)}
                onUpdate={handleUpdateCell}
                editingCell={editingCell}
                updatingCell={updatingCell}
            />

            {/* <ScheduleModal
                visible={scheduleModalOpen}
                onClose={() => {
                    setScheduleModalOpen(false);
                    scheduleForm.resetFields();
                }}
                onAssign={handleAssignSchedule}
                form={scheduleForm}
                assigningSchedule={assigningSchedule}
                extractedScheduleData={extractedScheduleData}
            /> */}
         <ScheduleModal
    visible={scheduleModalOpen}
    onClose={() => {
        setScheduleModalOpen(false);
        setEditingSchedule(null);
        setExtractedScheduleData(null);
    }}
    onSubmit={handleAssignSchedule}
    editingSchedule={editingSchedule}
    loading={assigningSchedule}
/>
        </>
    );
};

export default AddReportModal;